import { useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { acquireAccessToken } from '@/lib/msalConfig';
import { migrationKeys } from '@/lib/api';
import type { MigrationLog, UserMigrationPhaseStatus } from '@/types';

const HUB_URL = import.meta.env.VITE_SIGNALR_HUB_URL ?? '/hubs/migration-progress';

export interface ProgressUpdate {
  migrationId: string;
  moduleKey: string | null;
  phase?: 'addModule' | 'migrate' | undefined;
  percentage: number;
  message: string | null;
  timestamp: string;
}

export interface UserMigrationPhaseState {
  status: 'idle' | 'collecting' | 'onboarding' | 'complete';
  totalModules: number;
  totalRaw: number;
  totalUnique: number;
  moduleCounts: Record<string, number>;
  onboarded: number;
  failed: number;
  total: number;
  invitesSent: number;
  invitesFailed: number;
  phaseStatus: UserMigrationPhaseStatus | null;
}

const initialUserMigrationState: UserMigrationPhaseState = {
  status: 'idle',
  totalModules: 0,
  totalRaw: 0,
  totalUnique: 0,
  moduleCounts: {},
  onboarded: 0,
  failed: 0,
  total: 0,
  invitesSent: 0,
  invitesFailed: 0,
  phaseStatus: null,
};

interface UseMigrationProgressResult {
  progress: ProgressUpdate | null;
  logs: MigrationLog[];
  isConnected: boolean;
  error: Error | null;
  userMigrationPhase: UserMigrationPhaseState;
}

export function useMigrationProgress(migrationId: string): UseMigrationProgressResult {
  const queryClient = useQueryClient();
  const connectionRef = useRef<HubConnection | null>(null);
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);
  const [logs, setLogs] = useState<MigrationLog[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [userMigrationPhase, setUserMigrationPhase] = useState<UserMigrationPhaseState>(
    initialUserMigrationState
  );

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: async () => {
          const token = await acquireAccessToken();
          return token ?? '';
        },
      })
      .withAutomaticReconnect()
      .configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on('ProgressUpdated', (update: ProgressUpdate) => {
      if (update.migrationId === migrationId) {
        setProgress(update);
      }
    });

    connection.on('LogReceived', (log: MigrationLog) => {
      setLogs((prev) => [...prev.slice(-99), log]);
    });

    connection.on('MigrationCompleted', (data: { migrationId: string; status: string }) => {
      if (data.migrationId === migrationId) {
        queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
        queryClient.invalidateQueries({ queryKey: migrationKeys.lists() });
      }
    });

    connection.on('ModuleCompleted', (data: { migrationId: string; moduleKey: string }) => {
      if (data.migrationId === migrationId) {
        queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
      }
    });

    connection.on(
      'UserMigrationPhaseStarted',
      (data: { migrationId: string; totalModules: number }) => {
        if (data.migrationId !== migrationId) return;
        queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
        setUserMigrationPhase((prev) => ({
          ...prev,
          status: 'collecting',
          totalModules: data.totalModules,
        }));
      }
    );

    connection.on(
      'UserMigrationUsersCollected',
      (data: {
        migrationId: string;
        totalRaw: number;
        totalUnique: number;
        moduleCounts: Record<string, number>;
      }) => {
        if (data.migrationId !== migrationId) return;
        setUserMigrationPhase((prev) => ({
          ...prev,
          status: 'onboarding',
          totalRaw: data.totalRaw,
          totalUnique: data.totalUnique,
          moduleCounts: data.moduleCounts,
          total: data.totalUnique,
        }));
      }
    );

    connection.on(
      'UserMigrationProgress',
      (data: { migrationId: string; onboarded: number; failed: number; total: number }) => {
        if (data.migrationId !== migrationId) return;
        setUserMigrationPhase((prev) => ({
          ...prev,
          onboarded: data.onboarded,
          failed: data.failed,
          total: data.total,
        }));
      }
    );

    connection.on(
      'UserMigrationUuidsSent',
      (data: { migrationId: string; moduleKey: string; usersSent: number }) => {
        if (data.migrationId !== migrationId) return;
        // Refresh migration data so module status updates
        queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
      }
    );

    connection.on(
      'UserMigrationPhaseCompleted',
      (data: {
        migrationId: string;
        status: UserMigrationPhaseStatus;
        onboarded: number;
        failed: number;
        invitesSent: number;
        invitesFailed: number;
      }) => {
        if (data.migrationId !== migrationId) return;
        setUserMigrationPhase((prev) => ({
          ...prev,
          status: 'complete',
          phaseStatus: data.status,
          onboarded: data.onboarded,
          failed: data.failed,
          invitesSent: data.invitesSent,
          invitesFailed: data.invitesFailed,
        }));
        queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
      }
    );

    connection.onreconnected(() => {
      setIsConnected(true);
      setError(null);
      connection.invoke('JoinMigrationGroup', migrationId).catch(console.error);
    });

    connection.onclose((err) => {
      setIsConnected(false);
      if (err) setError(err);
    });

    connection
      .start()
      .then(() => {
        setIsConnected(true);
        return connection.invoke('JoinMigrationGroup', migrationId);
      })
      .catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => {
      if (connection.state === HubConnectionState.Connected) {
        connection.invoke('LeaveMigrationGroup', migrationId).catch(() => {});
      }
      connection.stop();
    };
  }, [migrationId, queryClient]);

  return { progress, logs, isConnected, error, userMigrationPhase };
}
